import { HALLE_KEY_NEEDS_WORKSPACE, HALLE_WORKSPACE_INVALID } from "custom-types";
import { FEATURE_DISABLED_MESSAGE } from "./features";

/**
 * Plain words for every Hall-E error code (claude/design/hall-e/02 §2.6,
 * "Error codes"). Errors arrive as `{code, detail, status_code}`. People
 * read these, so no code, field name or other technical detail is shown:
 * the server's `detail` is used only where it says what to fix in plain
 * words (an `INVALID` request, a refused workspace ID), and never when it
 * quotes a field.
 */
export const HALLE_ERROR_COPY: Readonly<Record<string, string>> = Object.freeze({
  FEATURE_DISABLED: FEATURE_DISABLED_MESSAGE,
  HALLE_PAUSED:
    "Hall-E is paused for the rest of the month because this month’s spending limit has been reached.",
  HALLE_KEY_FAILED:
    "Hall-E can’t answer because Anthropic did not accept your company’s key.",
  HALLE_NOT_READY: "Hall-E isn’t ready yet. Please try again later.",
  HALLE_SCOPE: "That hall is not one you have access to.",
  HALLE_BUSY:
    "Another answer is in progress. Wait for it to finish, then try again.",
  INVALID: "Please check what you entered and try again.",
  BYOK_INVALID:
    "Anthropic did not accept this key. Check that it was copied whole.",
  KEY_INVALID:
    "Anthropic did not accept this key. Check that it was copied whole.",
  TITLE_TAKEN: "A note with this title already exists. Choose another title.",
  NAMES_COMPANY:
    "This must not name any company or hall: it is used for every cinema.",
  SUPPORT_ACCESS: "Give a reason first. Access lasts two hours.",
  FORBIDDEN: "Only Picovico can change this.",
  NOT_FOUND: "This is no longer available.",
  HALLE_NOT_CONFIGURED:
    "Keys can’t be saved yet because the server is not set up for it. Please contact Picovico.",
  // Q32. The server's own sentence is shown first (`DETAIL_FIRST`); these
  // are for a refusal that comes without a plain one.
  [HALLE_KEY_NEEDS_WORKSPACE]:
    "This is an organization-wide key, so it needs a workspace ID. Add the ID of the Anthropic workspace to use, then save again.",
  [HALLE_WORKSPACE_INVALID]:
    "This workspace ID was not accepted. Copy it again from the Workspaces page of the Anthropic console, and check that the key may use that workspace.",
  NETWORK: "Hall-E could not be reached. Check the connection and try again.",
});

/**
 * Codes whose server `detail` says what to fix better than one fixed
 * sentence can, so a plain `detail` is shown as it is, and their copy
 * otherwise. The workspace refusals tell apart a malformed ID, one Anthropic
 * calls not valid and one it can't find for the key, and on the cpanel PAYG
 * key they name the Hall-E workspace; amphitheater keeps every one of them
 * plain (`halle_platform/workspaces.py`). `INVALID` works the same way, but
 * falls back to the screen's own sentence.
 */
const DETAIL_FIRST = new Set<string>([HALLE_KEY_NEEDS_WORKSPACE, HALLE_WORKSPACE_INVALID]);

/** How every Anthropic workspace ID starts (Q32). */
export const HALLE_WORKSPACE_ID_PREFIX = "wrkspc_";

/**
 * A workspace ID as amphitheater accepts it: `wrkspc_` and 8 to 64 letters
 * and digits (Anthropic's own IDs have 24).
 */
const WORKSPACE_ID = /^wrkspc_[A-Za-z0-9]{8,64}$/;

/**
 * What is wrong with a workspace ID as typed, or null when it can be sent.
 * Empty is fine: a key made inside a workspace needs none. The rule is the
 * server's, so a malformed ID never makes the round trip. The ID is not
 * kept secret (it is shown back after saving), so an API key pasted here by
 * mistake is stopped before it leaves the browser.
 */
export function halleWorkspaceIdProblem(value: string): string | null {
  const id = value.trim();
  if (!id) return null;
  if (id.startsWith("sk-ant-")) {
    return "That is an API key, not a workspace ID. Put it in the key field; a workspace ID starts with wrkspc_.";
  }
  if (!WORKSPACE_ID.test(id)) {
    return "A workspace ID is wrkspc_ followed by letters and digits. Copy it whole from the ID column on the Workspaces page of the Anthropic console.";
  }
  return null;
}

export interface HalleErrorParts {
  code: string;
  detail?: string;
}

/**
 * The `{code, detail}` of a refused Hall-E request: the body an RTK Query
 * error carries, or an axios response holding it. Null when it is neither.
 */
export function halleErrorParts(error: unknown): HalleErrorParts | null {
  const candidate = error as {
    code?: unknown;
    detail?: unknown;
    data?: { code?: unknown; detail?: unknown } | null;
  } | null;
  if (!candidate || typeof candidate !== "object") return null;
  const body =
    typeof candidate.code === "string" ? candidate : candidate.data ?? null;
  if (!body || typeof body.code !== "string" || !body.code) return null;
  return {
    code: body.code,
    detail: typeof body.detail === "string" ? body.detail : undefined,
  };
}

/**
 * Whether a refusal is about the workspace ID sent with a key (Q32), so a
 * screen can point at that field rather than the key's.
 */
export function isHalleWorkspaceRefusal(error: unknown): boolean {
  const code = halleErrorParts(error)?.code.toUpperCase();
  return code === HALLE_KEY_NEEDS_WORKSPACE || code === HALLE_WORKSPACE_INVALID;
}

/** A server sentence fit to show as it is: short, and quoting no field. */
export function plainHalleDetail(detail: unknown): string | null {
  if (typeof detail !== "string") return null;
  const text = detail.trim();
  if (!text || text.length > 240 || /[`{}<>_]/.test(text)) return null;
  return text;
}

/**
 * What to tell a person about a refused Hall-E request. `overrides` words a
 * code for one screen (for example a control-panel-only meaning).
 */
export function halleErrorMessage(
  error: unknown,
  fallback: string,
  overrides: Readonly<Record<string, string>> = {},
): string {
  const parts = halleErrorParts(error);
  if (!parts) return fallback;
  const code = parts.code.toUpperCase();
  if (overrides[code]) return overrides[code];
  if (code === "INVALID") return plainHalleDetail(parts.detail) ?? fallback;
  if (DETAIL_FIRST.has(code)) {
    return plainHalleDetail(parts.detail) ?? HALLE_ERROR_COPY[code];
  }
  return HALLE_ERROR_COPY[code] ?? plainHalleDetail(parts.detail) ?? fallback;
}
